import { useState, useEffect, useRef } from 'react';

/**
 * 에디터 내용 기반 페이지 수 계산 커스텀 훅
 * @param {string} editorContent - 에디터 내용
 * @param {boolean} isPreviewMode - 미리보기 모드 여부
 * @returns {Object} 페이지 수 및 ref
 */
function usePageCount(editorContent, isPreviewMode) {
  const [pageCount, setPageCount] = useState(1);
  const editorContentRef = useRef(null);
  const timeoutRef = useRef(null);
  // editorContent의 길이를 비교하여 실제 변경 여부 확인 (무한 루프 방지)
  const prevEditorContentLengthRef = useRef(editorContent ? editorContent.length : 0);

  useEffect(
    function () {
      // 미리보기 모드가 아니면 페이지 수를 1로 설정하고 종료
      if (!isPreviewMode) {
        setPageCount(1);
        prevEditorContentLengthRef.current = 0;
        return;
      }

      // editorContent의 길이가 변경되지 않았으면 실행하지 않음 (무한 루프 방지)
      const currentLength = editorContent ? editorContent.length : 0;
      if (prevEditorContentLengthRef.current === currentLength && currentLength > 0) {
        return;
      }
      prevEditorContentLengthRef.current = currentLength;

      // 이전 timeout이 있으면 clear
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // DOM이 업데이트된 후 높이 계산
      timeoutRef.current = setTimeout(function () {
        if (editorContentRef.current) {
          const contentHeight = editorContentRef.current.scrollHeight;
          // A4 세로의 60% (178.2mm)를 픽셀로 변환
          // 1mm = 3.7795275590551px (96 DPI 기준)
          const editorHeightPx = 178.2 * 3.7795275590551; // 약 673px
          const calculatedPageCount = Math.max(1, Math.ceil(contentHeight / editorHeightPx));

          setPageCount(function (prev) {
            // 이전 값과 동일하면 업데이트하지 않음 (무한 루프 방지)
            if (prev === calculatedPageCount) {
              return prev;
            }
            return calculatedPageCount;
          });
        } else {
          setPageCount(1);
        }
      }, 100);

      // cleanup 함수: 컴포넌트 언마운트 또는 의존성 변경 시 timeout clear
      return function () {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    },
    [editorContent, isPreviewMode],
  );

  return {
    pageCount,
    editorContentRef,
  };
}

export default usePageCount;
